<template>
  <div class="space-y-3">
    <div v-if="!activities || activities.length === 0" class="text-sm text-gray-400 text-center py-6">
      No activity yet.
    </div>
    <ol v-else class="relative border-l border-gray-200 ml-3">
      <li
        v-for="entry in sortedActivities"
        :key="entry.id"
        class="ml-4 pb-5"
        data-testid="transcript-activity-entry"
      >
        <span
          class="absolute -left-2 flex items-center justify-center w-4 h-4 rounded-full ring-4 ring-white"
          :class="iconBg(entry.type)"
        >
          <i :class="['pi text-[10px] text-white', iconName(entry.type)]"></i>
        </span>
        <div class="text-xs text-gray-400 flex items-center gap-2">
          <span>{{ formatDate(entry.created_at) }}</span>
          <span v-if="senderLabel(entry)" class="text-gray-500">· {{ senderLabel(entry) }}</span>
          <i
            v-if="entry.is_visible_to_applicant"
            v-tooltip.top="'Visible to applicant'"
            class="pi pi-eye text-primary text-xs"
          ></i>
        </div>
        <div class="mt-1 text-sm text-gray-700">{{ entry.content }}</div>
        <div
          v-if="entry.metadata?.attachment_name"
          class="mt-1 flex items-center gap-1 text-xs text-blue-600"
        >
          <i class="pi pi-paperclip text-xs"></i>
          <span>{{ entry.metadata.attachment_name }}</span>
        </div>
        <div
          v-if="entry.type === 'status_change' && (entry.old_status || entry.new_status)"
          class="mt-1 flex items-center gap-2 text-xs"
        >
          <TranscriptStatusBadge v-if="entry.old_status" :status="entry.old_status" />
          <i v-if="entry.old_status && entry.new_status" class="pi pi-arrow-right text-gray-400 text-xs"></i>
          <TranscriptStatusBadge v-if="entry.new_status" :status="entry.new_status" />
        </div>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
interface ActivityEntry {
  id: string;
  type: string;
  content: string;
  old_status?: string | null;
  new_status?: string | null;
  is_visible_to_applicant?: boolean;
  created_at: string;
  user?: { id: string; name: string } | null;
  metadata?: Record<string, unknown> | null;
}

const props = defineProps<{ activities: ReadonlyArray<ActivityEntry> | null | undefined }>();

const sortedActivities = computed(() =>
  [...(props.activities ?? [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
);

const senderLabel = (entry: ActivityEntry): string => {
  if (entry.user?.name) return entry.user.name;
  if (entry.type === 'message') {
    const meta = entry.metadata;
    if (meta?.student_name) return meta.student_name as string;
    if (meta?.applicant_name) return meta.applicant_name as string;
    return 'Applicant';
  }
  return '';
};

const iconName = (type: string) => {
  switch (type) {
  case 'status_change':
    return 'pi-sync';
  case 'note':
    return 'pi-pencil';
  case 'message':
    return 'pi-comment';
  default:
    return 'pi-circle';
  }
};

const iconBg = (type: string) => {
  switch (type) {
  case 'status_change':
    return 'bg-primary';
  case 'note':
    return 'bg-amber-500';
  case 'message':
    return 'bg-emerald-500';
  default:
    return 'bg-gray-400';
  }
};

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>
